import type { ReactNode } from 'react'
import {
  getFbcChannelLabel,
  type FbcChannelId,
  type FbcSignalState,
} from './fbc-design-grammar'
import { useLocaleContext } from '../../context/LocaleContext.js'

export type FutureDotGlyph =
  | FbcChannelId
  | 'video'
  | 'location'
  | 'file'
  | 'flip'
  | 'plus'
  | 'sun'
  | 'moon'
  | 'slash'

const GLYPHS: Record<FutureDotGlyph, number[][]> = {
  matrix: [
    [1, 0, 1, 0, 1],
    [0, 1, 0, 1, 0],
    [1, 0, 1, 0, 1],
    [0, 1, 0, 1, 0],
    [1, 0, 1, 0, 1],
  ],
  signal: [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [1, 0, 1, 0, 1],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
  ],
  voice: [
    [0, 0, 1, 0, 0],
    [0, 1, 1, 1, 0],
    [1, 1, 1, 1, 1],
    [0, 1, 1, 1, 0],
    [0, 0, 1, 0, 0],
  ],
  vision: [
    [0, 1, 0, 1, 0],
    [1, 0, 0, 0, 1],
    [0, 0, 1, 0, 0],
    [1, 0, 0, 0, 1],
    [0, 1, 0, 1, 0],
  ],
  video: [
    [1, 1, 1, 1, 1],
    [1, 0, 1, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 1, 0, 1],
    [1, 1, 1, 1, 1],
  ],
  screen: [
    [1, 1, 1, 1, 1],
    [1, 0, 0, 0, 1],
    [1, 0, 1, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 1, 1, 1, 1],
  ],
  location: [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [1, 0, 1, 0, 1],
    [0, 1, 0, 1, 0],
    [0, 0, 1, 0, 0],
  ],
  memory: [
    [1, 1, 0, 0, 0],
    [0, 1, 1, 0, 0],
    [0, 0, 1, 1, 0],
    [0, 1, 1, 0, 0],
    [1, 1, 0, 0, 0],
  ],
  research: [
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
    [0, 0, 0, 1, 1],
  ],
  export: [
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [1, 0, 1, 0, 1],
    [0, 1, 1, 1, 0],
    [1, 1, 1, 1, 1],
  ],
  report: [
    [1, 1, 1, 1, 1],
    [1, 0, 0, 0, 1],
    [1, 1, 1, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 1, 1, 1, 1],
  ],
  file: [
    [1, 1, 1, 0, 0],
    [1, 0, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [1, 0, 0, 0, 1],
    [1, 1, 1, 1, 1],
  ],
  flip: [
    [0, 0, 1, 0, 0],
    [1, 0, 0, 0, 1],
    [0, 1, 1, 1, 0],
    [1, 0, 0, 0, 1],
    [0, 0, 1, 0, 0],
  ],
  plus: [
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
    [1, 1, 1, 1, 1],
    [0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0],
  ],
  halt: [
    [1, 1, 1, 1, 1],
    [1, 1, 1, 1, 1],
    [1, 1, 1, 1, 1],
    [1, 1, 1, 1, 1],
    [1, 1, 1, 1, 1],
  ],
  transmit: [
    [0, 0, 1, 0, 0],
    [0, 0, 1, 1, 0],
    [1, 1, 1, 1, 1],
    [0, 0, 1, 1, 0],
    [0, 0, 1, 0, 0],
  ],
  phase: [
    [0, 0, 1, 0, 0],
    [1, 0, 1, 0, 1],
    [0, 1, 1, 1, 0],
    [1, 0, 1, 0, 1],
    [0, 0, 1, 0, 0],
  ],
  sun: [
    [0, 0, 1, 0, 0],
    [1, 0, 1, 0, 1],
    [0, 1, 1, 1, 0],
    [1, 0, 1, 0, 1],
    [0, 0, 1, 0, 0],
  ],
  moon: [
    [0, 0, 1, 1, 0],
    [0, 1, 0, 0, 0],
    [1, 0, 0, 0, 0],
    [0, 1, 0, 0, 0],
    [0, 0, 1, 1, 0],
  ],
  slash: [
    [0, 0, 0, 0, 1],
    [0, 0, 0, 1, 0],
    [0, 0, 1, 0, 0],
    [0, 1, 0, 0, 0],
    [1, 0, 0, 0, 0],
  ],
}

interface FutureDotMatrixIconProps {
  glyph: FutureDotGlyph
  state?: FbcSignalState
  size?: number
  className?: string
}

export function FutureDotMatrixIcon({
  glyph,
  state = 'idle',
  size = 16,
  className,
}: FutureDotMatrixIconProps) {
  const matrix = GLYPHS[glyph] ?? GLYPHS.signal
  const dotSize = Math.max(1.1, size / 12)
  const gap = Math.max(1.3, size / 18)

  return (
    <span
      className={`future-dot-matrix is-${state} ${className ?? ''}`}
      style={{
        width: size,
        height: size,
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        gap,
      }}
      aria-hidden="true"
    >
      {matrix.flatMap((row, rowIndex) =>
        row.map((filled, colIndex) => (
          <span
            key={`${rowIndex}-${colIndex}`}
            className={filled ? 'is-filled' : 'is-empty'}
            style={{ width: dotSize, height: dotSize }}
          />
        ))
      )}
    </span>
  )
}

interface FutureDotButtonProps {
  glyph: FutureDotGlyph
  label: string
  active?: boolean | undefined
  danger?: boolean | undefined
  disabled?: boolean | undefined
  onClick?: (() => void) | undefined
  className?: string | undefined
  showLabel?: boolean | undefined
  /** When set, the control is a link. A plain click can still open the sheet. */
  href?: string | undefined
}

export function FutureDotButton({
  glyph,
  label,
  active = false,
  danger = false,
  disabled = false,
  onClick,
  className,
  showLabel = false,
  href,
}: FutureDotButtonProps) {
  const classNames = `future-dot-button ${active ? 'is-active' : ''} ${danger ? 'is-danger' : ''} ${disabled ? 'is-disabled' : ''} ${className ?? ''}`
  const icon = <FutureDotMatrixIcon glyph={glyph} state={active ? 'signal' : 'idle'} />
  if (href && !disabled) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        title={label}
        className={classNames}
        onClick={event => {
          if (!onClick) return
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return
          event.preventDefault()
          onClick()
        }}
      >
        {icon}
        {showLabel && <span>{label}</span>}
      </a>
    )
  }
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={classNames}
    >
      {icon}
      {showLabel && <span>{label}</span>}
    </button>
  )
}

type DotPrimitiveProps = Omit<FutureDotMatrixIconProps, 'glyph'>

export function DotGrid(props: DotPrimitiveProps) {
  return <FutureDotMatrixIcon glyph="matrix" {...props} />
}

export function DotWave(props: DotPrimitiveProps) {
  return <FutureDotMatrixIcon glyph="voice" {...props} />
}

export function DotOrbit(props: DotPrimitiveProps) {
  return <FutureDotMatrixIcon glyph="research" {...props} />
}

export function DotScreen(props: DotPrimitiveProps) {
  return <FutureDotMatrixIcon glyph="screen" {...props} />
}

export function DotMemory(props: DotPrimitiveProps) {
  return <FutureDotMatrixIcon glyph="memory" {...props} />
}

export function DotVision(props: DotPrimitiveProps) {
  return <FutureDotMatrixIcon glyph="vision" {...props} />
}

export function DotSignalButton(props: FutureDotButtonProps) {
  return <FutureDotButton {...props} />
}

interface FutureStatusPillProps {
  glyph?: FutureDotGlyph
  active?: boolean
  children: ReactNode
  className?: string
}

export function FutureStatusPill({
  glyph = 'signal',
  active = false,
  children,
  className,
}: FutureStatusPillProps) {
  return (
    <span className={`future-status-pill ${active ? 'is-active' : ''} ${className ?? ''}`}>
      <FutureDotMatrixIcon glyph={glyph} state={active ? 'signal' : 'idle'} size={12} />
      <span>{children}</span>
      {active && <span className="future-live-dot" aria-hidden="true" />}
    </span>
  )
}

interface FutureChannelControlProps {
  channel: FbcChannelId
  active?: boolean
  onClick?: () => void
  className?: string
}

export function FutureChannelControl({
  channel,
  active = false,
  onClick,
  className,
}: FutureChannelControlProps) {
  const { t } = useLocaleContext()

  return (
    <FutureDotButton
      glyph={channel}
      label={getFbcChannelLabel(channel, t)}
      active={active}
      {...(onClick ? { onClick } : {})}
      {...(className ? { className } : {})}
    />
  )
}

interface FutureSignalFieldProps {
  id: string
  placeholder?: string
  value?: string
}

export function FutureSignalField({
  id,
  placeholder = 'Message F.B/c…',
  value = '',
}: FutureSignalFieldProps) {
  return (
    <div className="future-signal-field">
      <span className="future-signal-field-rail" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
      <label htmlFor={id} className="sr-only">
        Message F.B/c
      </label>
      <input id={id} type="text" readOnly value={value} placeholder={placeholder} />
      <span className="future-signal-cursor" aria-hidden="true" />
    </div>
  )
}

export function FutureSignalMeter({ className }: { className?: string }) {
  return (
    <span className={`future-signal-meter ${className ?? ''}`} aria-hidden="true">
      <span className="is-live w-[14px]" />
      <span className="is-live w-[10px]" />
      <span className="is-live w-[20px]" />
      <span className="w-[24px]" />
    </span>
  )
}

